import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { GlobalFilters } from '@aurora-pvi/contracts';

/**
 * Global filters, persisted in the URL.
 *
 * The URL is the source of truth rather than a store, for three reasons the
 * specification insists on: a filtered view must be shareable as a link, a
 * drill-down must inherit the filters that produced it, and an export must
 * reproduce exactly what the user was looking at. Keeping the state anywhere
 * else makes all three accidental instead of guaranteed.
 */

interface FiltersValue {
  filters: GlobalFilters;
  setFilter: <K extends keyof GlobalFilters>(key: K, value: GlobalFilters[K]) => void;
  setFilters: (next: Partial<GlobalFilters>) => void;
  clearFilter: (key: keyof GlobalFilters) => void;
  clearAll: () => void;
  /** Serialised query string, for building drill-down links that inherit state. */
  searchString: string;
  activeCount: number;
}

const FiltersContext = createContext<FiltersValue | null>(null);

const ARRAY_KEYS = new Set<keyof GlobalFilters>([
  'departmentIds',
  'valueStreamIds',
  'productGroupIds',
  'productIds',
  'ownerIds',
  'health',
  'lifecycle',
  'classification',
]);

/**
 * Both spellings of a multi-valued filter are read.
 *
 * This wrote `key = value.split(',')` inside a loop over `entries()`, so a URL
 * repeating a key — `?health=HighRisk&health=Critical`, which is what the API
 * client itself emits — kept only the last one seen. A drill-down onto "the 36
 * products at risk" landed on the 18 that are critical, and the screen looked
 * like it disagreed with the tile that opened it.
 *
 * `getAll` collects every occurrence, and each is still split on commas so a
 * hand-written or bookmarked `?health=HighRisk,Critical` keeps working.
 */
function parseFilters(params: URLSearchParams): GlobalFilters {
  const result: Record<string, unknown> = {};
  for (const key of new Set(params.keys())) {
    if (ARRAY_KEYS.has(key as keyof GlobalFilters)) {
      const values = params
        .getAll(key)
        .flatMap((value) => value.split(','))
        .filter(Boolean);
      if (values.length) result[key] = values;
    } else {
      const value = params.get(key);
      if (value) result[key] = value;
    }
  }
  return result as GlobalFilters;
}

export function FiltersProvider({ children }: { children: ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => parseFilters(searchParams), [searchParams]);

  const setFilters = useCallback(
    (next: Partial<GlobalFilters>) => {
      setSearchParams(
        (previous) => {
          const updated = new URLSearchParams(previous);
          for (const [key, value] of Object.entries(next)) {
            if (value === undefined || value === null || value === '') {
              updated.delete(key);
              continue;
            }
            updated.set(key, Array.isArray(value) ? value.join(',') : String(value));
          }
          return updated;
        },
        // Filter changes replace rather than push: the back button should leave
        // the screen, not step backwards through twelve filter tweaks.
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setFilter = useCallback<FiltersValue['setFilter']>(
    (key, value) => setFilters({ [key]: value } as Partial<GlobalFilters>),
    [setFilters],
  );

  const clearFilter = useCallback(
    (key: keyof GlobalFilters) => setFilters({ [key]: undefined } as Partial<GlobalFilters>),
    [setFilters],
  );

  const clearAll = useCallback(
    () => setSearchParams(new URLSearchParams(), { replace: true }),
    [setSearchParams],
  );

  const value = useMemo<FiltersValue>(
    () => ({
      filters,
      setFilter,
      setFilters,
      clearFilter,
      clearAll,
      searchString: searchParams.toString(),
      activeCount: Object.keys(filters).length,
    }),
    [filters, setFilter, setFilters, clearFilter, clearAll, searchParams],
  );

  return <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>;
}

export function useFilters(): FiltersValue {
  const context = useContext(FiltersContext);
  if (!context) {
    throw new Error('useFilters must be used within a <FiltersProvider>.');
  }
  return context;
}
